import * as React from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';

import MoviesEdit from '@/pages/movies/MoviesEdit';

import NoPermissionPage from '../components/no-permission-page/no-permission-page';
import OopsPage from '../components/Oops-Page/OopsPage';
import AuthScreen from '../pages/auth/auth-screen';
import Providers from '../providers';
import lazyRetry from './lazyRetry';
import ProtectedRoute from './ProtectedRoute';

const Dashboard = React.lazy(() => lazyRetry(() => import('../pages/dashboard/dashboard')));
const Assets = React.lazy(() => lazyRetry(() => import('../pages/assets/assets')));
const AssetListLoader = React.lazy(() => import('../pages/assets/asset-list-loader'));
const AssetEditor = React.lazy(() => import('../pages/assets/asset-editor/asset-editor'));
const ChannelManager = React.lazy(() => lazyRetry(() => import('../pages/channelmanager')));
const Channels = React.lazy(() => import('../pages/channelmanager/channels'));
const ChannelList = React.lazy(() => import('../pages/channelmanager/channels/channel-list'));
const LivePrograms = React.lazy(() => import('../pages/live-programs'));
const AppAnalytics = React.lazy(() => import('../pages/AppAnalytics/AppAnalytics'));

// Powr Site Pages
const Site = React.lazy(() => import('../pages/site/site'));
const SiteProviders = React.lazy(() => import('../pages/site/SiteProviders'));
const SiteAppSettings = React.lazy(() => import('../pages/site/app/settings'));
const SiteGeneralSettings = React.lazy(() => import('../pages/site/general/settings'));
const SiteLayoutSettings = React.lazy(() => import('../pages/site/layout/LayoutSettings'));
const SiteAppPlatformsSettings = React.lazy(() => import('../pages/site/platforms/settings'));
const SiteIntegrations = React.lazy(() => import('../pages/site/integrations/settings'));
const SiteContent = React.lazy(() => import('../pages/site/content/settings'));
const SiteCheckout = React.lazy(() => import('../pages/site/checkout/settings'));
const SiteAdsSettings = React.lazy(() => import('../pages/site/ads/settings'));

const MaintenanceWindow = React.lazy(() => import('../pages/MaintenanceWindow/MaintenanceWindow'));
const LiveProgramList = React.lazy(() => import('../pages/live-programs/live-program-list'));
const TransitionTemplates = React.lazy(() => import('../pages/transition-templates'));
const TransitionTemplateList = React.lazy(() => import('../pages/transition-templates/transition-template-list'));
const TransitionTemplateEditor = React.lazy(() => import('../pages/transition-templates/transition-template-editor'));
const Admin = React.lazy(() => import('../pages/Admin'));
const LegacyAdmin = React.lazy(() => import('../pages/LegacyAdmin/Admin'));
const NewAnalytics = React.lazy(() => import('../pages/Admin/NewAnalytics'));
const AdTagsManager = React.lazy(() => import('../pages/adtagsmanager'));
const MonitoringDashboard = React.lazy(() => import('../pages/MonitoringDashboard/MonitoringDashboard'));
const LiveProgramEditor = React.lazy(() => import('../pages/live-programs/live-program-editor'));
const ChannelEditor = React.lazy(() => import('../pages/channelmanager/channels/channel-editor'));
const UserManagementContainer = React.lazy(() => import('../pages/usermanagement/usermanagementcontainer'));
const Inputs = React.lazy(() => import('../pages/Inputs/Inputs'));
const InputList = React.lazy(() => import('../pages/Inputs/InputList'));
const InputEditor = React.lazy(() => import('../pages/Inputs/InputEditor'));
const Series = React.lazy(() => import('../pages/series/series'));
const SeriesListLoader = React.lazy(() => import('../pages/series/series-list-loader'));
const SeriesEditor = React.lazy(() => import('../pages/series/SeriesEditor/SeriesEditor'));
const UIComponents = React.lazy(() => import('../pages/UIComponents/UIComponents'));
const WorkflowConfigPage = React.lazy(() => import('../pages/workflow/config/WorkflowConfigPage'));
const DestinationsDashboard = React.lazy(() => import('../pages/workflow/viewer/DestinationsDashboard'));
const Movies = React.lazy(() => import('../pages/movies/Movies'));
const MoviesLoader = React.lazy(() => import('../pages/movies/MoviesLoader'));
const BillingAccount = React.lazy(() => import('../pages/billingaccount/BillingAccount'));
const Products = React.lazy(() => import('../pages/products/Products'));
const ProductsLoader = React.lazy(() => import('../pages/products/ProductsLoader'));
const ProductEditor = React.lazy(() => import('../pages/products/ProductEditor/ProductEditor'));
const Coupons = React.lazy(() => import('../pages/coupons/Coupons'));
const CouponsLoader = React.lazy(() => import('../pages/coupons/CouponsLoader'));
const CouponsList = React.lazy(() => import('../pages/coupons/CouponsList'));
const CouponEditor = React.lazy(() => import('../pages/coupons/CouponEditor/CouponEditor'));
const FeedConfig = React.lazy(() => import('../pages/feedconfig/FeedConfigContainer'));
const MapperTemplate = React.lazy(() => import('../pages/mappertemplate/MapperTemplateContainer'));
const AdConfig = React.lazy(() => import('../pages/adconfig/AdConfigContainer'));
const AdvancedAdmin = React.lazy(() => import('../pages/advancedadmin/AdvancedAdmin'));
const OuNavigation = React.lazy(() => import('../pages/ounavigation/OuNavigation'));
const AuditTrail = React.lazy(() => import('../pages/AuditTrail/AuditTrail'));
const AppEvent = React.lazy(() => import('../pages/AppEvent/AppEvent'));
const AppEventLoader = React.lazy(() => import('../pages/AppEvent/AppEventLoader'));
const AppEventEditor = React.lazy(() => import('../pages/AppEvent/AppEventEditor/AppEventEditor'));
const AppUsers = React.lazy(() => import('../pages/AppUsers/AppUsers'));
const AppUserLoader = React.lazy(() => import('../pages/AppUsers/AppUserLoader'));
const AppUserEditor = React.lazy(() => import('../pages/AppUsers/AppUserEditor/AppUserEditor'));
const LiveGuide = React.lazy(() => import('../pages/live-guide'));
const LiveGuideList = React.lazy(() => import('../pages/live-guide/live-guide-list'));
const LiveGuideEditor = React.lazy(() => import('../pages/live-guide/live-guide-editor'));
const EventWorkFlowLoader = React.lazy(() => import('../pages/EventWorkflow/EventWorkFlowLoader'));
const NotificationTemplate = React.lazy(() => import('../pages/EventWorkflow/NotificationTemplate'));

const Marketing = React.lazy(() => import('../pages/Marketing/Marketing'));
const PushNotificationsLoader = React.lazy(() => import('../pages/Marketing/PushNotificationsLoader'));

const Health = React.lazy(() => import('../pages/HealthDashboard/HealthLoader'));

const routes = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Providers />}>
      {/* no authentication required */}
      <Route path="nopermissions" element={<NoPermissionPage />} />
      <Route path="oops" element={<OopsPage />} />
      <Route path="maintenance" element={<MaintenanceWindow />} />
      <Route path="auth" element={<AuthScreen />} />

      <Route element={<ProtectedRoute />}>
        <Route path="dashboards">
          <Route path="analytics" element={<Dashboard />} />
          <Route path="stream-monitoring" element={<MonitoringDashboard />} />
          <Route path="usage" element={<NewAnalytics />} />
          <Route path="destinations" element={<DestinationsDashboard />} />
          <Route path="app-analytics" element={<AppAnalytics />} />
          <Route path="health" element={<Health />} />
        </Route>
        <Route path="legacy-admin" element={<LegacyAdmin />} />
        <Route path="ads" element={<AdTagsManager />} />
        <Route path="demo" element={<UIComponents />} />
        <Route path="assets" element={<Assets />}>
          <Route path="new" element={<AssetEditor />} />
          <Route path="list" element={<AssetListLoader />} />
        </Route>
        <Route path="live">
          <Route path="inputs" element={<Inputs />}>
            <Route path="list" element={<InputList />} />
            <Route path="editor/:inputId" element={<InputEditor />} />
          </Route>
          <Route path="programs" element={<LivePrograms />}>
            <Route path="editor/:liveProgramId" element={<LiveProgramEditor />} />
            <Route path="list" element={<LiveProgramList />} />
          </Route>
          <Route path="events" element={<AppEvent />}>
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<AppEventLoader />} />
            {/* // load site configs */}
            <Route path="" element={<SiteProviders />}>
              <Route path="editor/:appEventId" element={<AppEventEditor />} />
            </Route>
          </Route>
          <Route path="guide" element={<LiveGuide />}>
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<LiveGuideList />} />
            <Route path="editor/:liveGuideId" element={<LiveGuideEditor />} />
          </Route>
        </Route>
        <Route path="series" element={<Series />}>
          <Route path="editor/:id" element={<SeriesEditor />} />
          <Route path="list" element={<SeriesListLoader />} />
          <Route path="seasons/:id" element={<SeriesListLoader />} />
        </Route>
        <Route path="channelmanager" element={<ChannelManager />}>
          <Route element={<Channels />}>
            <Route path="editor/:channelId" element={<ChannelEditor />} />
            <Route path="list" element={<ChannelList />} />
          </Route>
        </Route>
        <Route path="site" element={<Site />}>
          <Route path="general" element={<SiteGeneralSettings />} />
          <Route path="layout" element={<SiteLayoutSettings />}>
            <Route path=":layoutItemUrl" element={<SiteLayoutSettings />} />
          </Route>
          <Route path="design" element={<SiteAppSettings />} />
          <Route path="platforms" element={<SiteAppPlatformsSettings />} />
          <Route path="content" element={<SiteContent />} />
          <Route path="ads" element={<SiteAdsSettings />} />
          <Route path="integrations" element={<SiteIntegrations />} />
          <Route path="checkout" element={<SiteCheckout />} />
        </Route>
        <Route path="transitiontemplates" element={<TransitionTemplates />}>
          <Route path="list" element={<TransitionTemplateList />} />
          <Route path="editor/:templateId" element={<TransitionTemplateEditor />} />
        </Route>

        <Route path="globalInterstitials" element={<Channels />}>
          <Route path="editor/:channelId" element={<ChannelEditor />} />
        </Route>
        <Route path="workflow-config" element={<WorkflowConfigPage />} />
        <Route path="movies" element={<Movies />}>
          <Route path="list" element={<MoviesLoader />} />
          <Route path="editor/:id" element={<MoviesEdit />} />
        </Route>
        <Route path="monetization/products" element={<Products />}>
          <Route path="editor/:id" element={<ProductEditor />} />
          <Route path="list" element={<ProductsLoader />} />
        </Route>
        <Route path="monetization/coupons" element={<Coupons />}>
          <Route index element={<Navigate to="list" replace />} />
          <Route path="list" element={<CouponsList />} />
          <Route path="editor/:id" element={<CouponEditor />} />
        </Route>
        <Route path="settings">
          <Route path="admin" element={<Admin />} />
          <Route path="mrss-feed-config" element={<FeedConfig />} />
          <Route path="mrss-mapper" element={<MapperTemplate />} />
          <Route path="ad-config" element={<AdConfig />} />
          <Route path="advanced-admin" element={<AdvancedAdmin />} />
          <Route path="ou-navigation" element={<OuNavigation />} />
        </Route>
        <Route path="account">
          <Route path="cms-user-management" element={<UserManagementContainer />} />
          <Route path="app-users" element={<AppUsers />}>
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<AppUserLoader />} />
            <Route path="editor/:appUserId" element={<AppUserEditor />} />
          </Route>
          <Route path="audit-trail" element={<AuditTrail />} />
          <Route path="billing" element={<BillingAccount />} />
        </Route>
        <Route path="event-workflows" element={<EventWorkFlowLoader />}>
          {/* <Route path="list" element={<PushNotificationLoader />} /> */}
          <Route path=":TabId" element={<NotificationTemplate />} />
        </Route>
        <Route path="marketing" element={<Marketing />}>
          <Route path="push-notifcations" element={<PushNotificationsLoader />} />
        </Route>
        <Route path="*" element={<Navigate to="/assets/list" />} />
      </Route>
    </Route>
  )
);

export default routes;
